import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Table,
  Col,
  message,
} from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import Filter from "../../../components/Filter";
import dayjs from "dayjs";

function OffersList() {
  const [form] = Form.useForm();
  const API_URL = import.meta.env.VITE_API_URL;
  const accessToken = useSelector((state) => state.user.accessToken);
  const [offers, setOffers] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentOffer, setCurrentOffer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const pharmacyDetails = useSelector((state) => state.user.pharmacyDetails);
  const userType = useSelector((state) => state.user?.user?.roleDetails?.id);
  const [filterDropdowns, setFilterDropdowns] = useState([
    {
      title: "Pharmacy",
      options: [],
    },
  ]);
  const [selectedFilters, setSelectedFilters] = useState({
    pharmacyId: null,
    search: null,
  });
  const [filterOptions, setFilterOptions] = useState([]);

  useEffect(() => {
    fetchPharmacies();
    fetchOffers();
  }, []);

  useEffect(() => {
    if (isModalVisible) {
      if (currentOffer) {
        const selectedPharmacy = pharmacies.find(
          (pharmacy) => pharmacy.id === currentOffer?.pharmacyID
        );
        form.setFieldsValue({
          title: currentOffer?.title,
          title_ar: currentOffer?.title_ar,
          description: currentOffer?.description,
          description_ar: currentOffer?.description_ar,
          startDate: currentOffer?.startDate
            ? moment(currentOffer.startDate)
            : null,
          endDate: currentOffer?.endDate ? moment(currentOffer.endDate) : null,
          pharmacy: selectedPharmacy ? selectedPharmacy.name : undefined,
        });
      } else {
        if (userType === 4) {
          form.setFieldsValue({
            pharmacy: pharmacyDetails?.name,
          });
        }
      }
    }
  }, [isModalVisible, currentOffer, pharmacies, userType, pharmacyDetails]);

  const fetchPharmacies = async () => {
    try {
      const pharmacyResponse = await axios.get(
        `${API_URL}pharmacies/get-pharmacies?limit=1000`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      const pharmacyList = pharmacyResponse.data.Pharmacies.map(
        (pharmacy) => pharmacy.name
      );
      const pharmacyListID = pharmacyResponse.data.Pharmacies.map(
        (pharmacy) => ({
          id: pharmacy.id,
          name: pharmacy.name,
        })
      );

      setPharmacies(pharmacyListID);
      setFilterDropdowns([
        {
          title: "Pharmacy",
          options: pharmacyList,
        },
      ]);
    } catch (error) {
      console.error("Error fetching pharmacies:", error);
      message.error("Failed to load pharmacy data.");
    }
  };

  const fetchOffers = async (
    page = 1,
    pageSize = 10,
    pharmacyId = null,
    search = null
  ) => {
    setLoading(true);
    try {
      let pharmacyIDParam;
      if (userType === 4) {
        pharmacyIDParam = pharmacyDetails?.id;
      } else {
        pharmacyIDParam = pharmacyId;
      }

      const params = {
        limit: pageSize,
        page: page,
        ...(pharmacyIDParam && { pharmacyID: pharmacyIDParam }),
        ...(search && { search }),
      };

      const response = await axios.get(`${API_URL}Offers/get-Offers`, {
        params,
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      setOffers(response.data.AllOffers);
      setPagination({
        current: page,
        pageSize: pageSize,
        total: response.data.totalCount,
      });
    } catch (error) {
      console.error("Error fetching offers:", error);
      message.error("Failed to fetch offers");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    const startDate = dayjs(values.startDate).format("YYYY-MM-DD");
    const endDate = dayjs(values.endDate).format("YYYY-MM-DD");

    if (moment(endDate).isSameOrBefore(startDate)) {
      message.error("End date must be greater than start date.");
      return;
    }

    let selectedPharmacyID;
    if (userType === 4) {
      selectedPharmacyID = pharmacyDetails.id;
    } else {
      selectedPharmacyID = pharmacies.find(
        (ph) => ph.name === values.pharmacy
      )?.id;
    }

    let payload = {};

    if (currentOffer) {
      if (values.title !== currentOffer.title) payload.title = values.title;
      if (values.title_ar !== currentOffer.title_ar)
        payload.title_ar = values.title_ar;
      if (values.description !== currentOffer.description)
        payload.description = values.description;
      if (values.description_ar !== currentOffer.description_ar)
        payload.description_ar = values.description_ar;
      if (startDate !== moment(currentOffer.startDate).format("YYYY-MM-DD"))
        payload.startDate = startDate;
      if (endDate !== moment(currentOffer.endDate).format("YYYY-MM-DD"))
        payload.endDate = endDate;
      if (selectedPharmacyID !== currentOffer.PharmacyDetails?.id)
        payload.pharmacyID = selectedPharmacyID;

      if (Object.keys(payload).length === 0) {
        message.info("No changes detected.");
        setIsModalVisible(false);
        return;
      }
    } else {
      payload = {
        title: values.title,
        title_ar: values.title_ar,
        startDate,
        endDate,
        pharmacyID: selectedPharmacyID,
        description: values.description,
        description_ar: values.description_ar,
      };
    }

    try {
      if (currentOffer) {
        await axios.patch(`${API_URL}Offers?id=${currentOffer.id}`, payload, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        message.success("Offer updated successfully");
      } else {
        await axios.post(`${API_URL}Offers`, payload, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        message.success("Offer created successfully");
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchOffers();
    } catch (error) {
      console.error("Error saving offer:", error);
      message.error(`Failed to ${currentOffer ? "update" : "create"} offer`);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}Offers?id=${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      message.success("Offer deleted successfully");
      fetchOffers();
    } catch (error) {
      console.error("Error deleting offer:", error);
      message.error("Failed to delete offer");
    }
  };

  const handleFilterChange = (key, value) => {
    let updatedFilters = { ...selectedFilters };
    let errorMessageShown = false;

    if (key === "Pharmacy") {
      const pharmacyId = value
        ? pharmacies.find((ph) => ph.name === value)?.id
        : null;
      updatedFilters.pharmacyId = pharmacyId;
    }

    if (key === "search") {
      let search = null;

      if (value && value.trim().length > 0) {
        if (value.length > 2) {
          search = value.trim(); // Keep the search term as a string
        }
      }

      updatedFilters.search = search;
    }

    if (key === "") {
      fetchOffers();
      updatedFilters = {};
    }

    setSelectedFilters(updatedFilters);

    fetchOffers(
      1,
      pagination.pageSize,
      updatedFilters.pharmacyId,
      updatedFilters.search
    );
  };

  const columns = [
    { title: "Title", dataIndex: "title", key: "title" },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      render: (date) => moment(date).format("YYYY-MM-DD"),
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      render: (date) => moment(date).format("YYYY-MM-DD"),
    },
    {
      title: "Pharmacy",
      dataIndex: ["PharmacyDetails", "name"],
      key: "pharmacy",
    },
    {
      title: "View",
      render: (_, record) => (
        <Button
          onClick={() => {
            setCurrentOffer(record);
            setIsModalVisible(true);
          }}
        >
          View
        </Button>
      ),
    },
    {
      title: "Delete",
      render: (_, record) => (
        <Button
          style={{
            borderRadius: 0,
            borderColor: "#FF4D4F",
            backgroundColor: "#FF4D4F",
            color: "#FFFFFF",
          }}
          danger
          onClick={() => handleDelete(record.id)}
        >
          Delete
        </Button>
      ),
    },
  ];
  useEffect(() => {
    fetchOffers(
      1,
      pagination.pageSize,
      selectedFilters.pharmacyId,
      selectedFilters.search
    );
  }, [pagination.pageSize, selectedFilters]);
  return (
    <div className="space-y-5">
      <Filter
        filterOptions={filterOptions}
        setFilterOptions={setFilterOptions}
        dropdowns={filterDropdowns}
        addItemName="Add Offer"
        resetFilter={false}
        filterBy={true}
        addItemAction={() => {
          setCurrentOffer(null);
          setIsModalVisible(true);
        }}
        onFilterChange={handleFilterChange}
        searchBarPlaceholder="Search Offers"
      />

      <div className="table-responsive-wrapper responsive-table-container">
        <Table
          className="customTable"
          columns={columns}
          dataSource={offers}
          loading={loading}
          rowKey="id"
          pagination={pagination}
          onChange={(pagination) =>
            fetchOffers(pagination.current, pagination.pageSize)
          }
        />
      </div>

      <Modal
        title={currentOffer ? "View Offer" : "Add Offer"}
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setCurrentOffer(null);
          form.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Title (EN)"
                name="title"
                rules={[{ required: true, message: "Please enter title" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Title (AR)"
                name="title_ar"
                rules={[{ required: true, message: "Please enter title" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Pharmacy"
                name="pharmacy"
                rules={[{ required: true, message: "Please select pharmacy" }]}
              >
                {userType === 4 ? (
                  <Input disabled value={pharmacyDetails?.name} />
                ) : (
                  <Select showSearch optionFilterProp="children">
                    {pharmacies.map((pharmacy) => (
                      <Select.Option key={pharmacy.id} value={pharmacy.name}>
                        {pharmacy.name}
                      </Select.Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Start Date"
                name="startDate"
                rules={[
                  { required: true, message: "Please select start date" },
                ]}
              >
                <DatePicker
                  prefix={<img src="/assets/images/date.svg" alt="calendar icon" />}
                  suffixIcon={null}
                  style={{ width: "100%" }}
                  onOpenChange={(open) => {
                    if (open) {
                      form.setFieldsValue({ endDate: null });
                      form.setFieldsValue({ startDate: null });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="End Date"
                name="endDate"
                rules={[
                  { required: true, message: "Please select end date" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || value.isAfter(getFieldValue("startDate"))) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("End date must be after start date")
                      );
                    },
                  }),
                ]}
              >
                <DatePicker
                  prefix={<img src="/assets/images/date.svg" alt="calendar icon" />}
                  suffixIcon={null}
                  style={{ width: "100%" }}
                  onOpenChange={(open) => {
                    if (open) {
                      form.setFieldsValue({ endDate: null });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Description (EN)"
                name="description"
                rules={[
                  { required: true, message: "Please enter description" },
                ]}
              >
                <Input.TextArea rows={4} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Description (AR)"
                name="description_ar"
                rules={[
                  { required: true, message: "Please enter description" },
                ]}
              >
                <Input.TextArea rows={4} />
              </Form.Item>
            </Col>
          </Row>
          <div className="flex justify-end gap-4">
            <Button onClick={() => setIsModalVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              {currentOffer ? "Update" : "Create"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

export default OffersList;
